import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import CloseIcon from "@mui/icons-material/Close";

import PageLoader from "components/common/PageLoader";
import ProtoAvatar from "components/Screens/Marketplace/Customer/common/ProtoAvatar/ProtoAvatar";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { getTalentDisplayName } from "components/Screens/Marketplace/Customer/utils/redaction";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import MeetingIcon from "plg/meeting/MeetingIcon";
import type { MeetingContextValue } from "plg/meeting/MeetingContext";
import "./Shortlist.scss";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";
import ToolChips from "components/Screens/Marketplace/Customer/common/ToolChips/ToolChips";
import UnavailablePill, { isUnavailable } from "components/Screens/Marketplace/Customer/Shortlist/UnavailablePill";
import useCustomerTools from "components/Screens/Marketplace/Customer/utils/useCustomerTools";
import speaksLabel from "components/Screens/Marketplace/Customer/utils/speaksLabel";

/** PLG shortlist (prototype renderShortlist): the people who look right, and one CTA, Request a meeting. */
function MeetingShortlist({ meeting }: { meeting: MeetingContextValue }) {
  const navigate = useNavigate();
  const { loading, items, count, remove } = useShortlist();
  const { routes } = useTalentApi();
  const myTools = useCustomerTools();
  const openProfile = (id: string) => navigate(routes.talentProfile(id));
  const { sent } = meeting;

  const copy = sent ? (
    <>
      <b>Your meeting request is with Edge</b>We will be in touch to arrange a time.
    </>
  ) : count ? (
    <>
      <b>{`${count} shortlisted`}</b>We will use these to understand what you are looking for.
    </>
  ) : (
    <>
      <b>No one shortlisted yet</b>Find someone in the talent network, or tell us who you need.
    </>
  );

  return (
    <div className="sl-page">
      <Helmet>
        <title>Edge | Shortlist</title>
      </Helmet>
      {loading && !items.length && <PageLoader />}
      <div className="sl-page__col">
        <div className="sl-page__head sl-meet-head">
          <div>
            <h1>Your shortlist</h1>
            <p>Profiles you’ve saved, in one place. No scrolling back to find them.</p>
          </div>
        </div>

        <div className="sl-count plg-card sl-meet-count">
          <div className="sl-count__copy">{copy}</div>
          <div className="sl-meet-cta">
            {sent ? (
              <span className="sl-meet-btn sl-meet-btn--soft is-sent">
                <MeetingIcon name="check" size={16} />
                Meeting requested
              </span>
            ) : (
              <button
                type="button"
                className={`sl-meet-btn ${count ? "sl-meet-btn--primary" : "sl-meet-btn--soft"}`}
                onClick={() => meeting.open("shortlist")}
              >
                <MeetingIcon name="calCheck" size={19} />
                Request a meeting
              </button>
            )}
          </div>
        </div>

        {count > 0 ? (
          <div className="sl-list">
            {items.map(({ talent }) => {
              // Left the talent network since (e.g. ASSIGNED): no profile to open, only the remove button.
              const gone = isUnavailable(talent);
              const name = getTalentDisplayName(talent);
              // eslint-disable-next-line no-underscore-dangle
              const open = () => openProfile(talent._id);
              return (
                // The whole card opens the profile (as on the talent network); the cross removes them.
                <div
                  // eslint-disable-next-line no-underscore-dangle
                  key={talent._id}
                  className={`sl-card plg-card sl-meet-card${gone ? " sl-card--unavailable" : " sl-meet-card--link"}`}
                  role={gone ? undefined : "link"}
                  tabIndex={gone ? undefined : 0}
                  aria-label={gone ? undefined : `View ${name}'s profile`}
                  onClick={gone ? undefined : open}
                  onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    if (gone || e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                    e.preventDefault();
                    open();
                  }}
                >
                  <span className="sl-meet-av">
                    <ProtoAvatar talent={talent} size={48} />
                  </span>
                  <div className="sl-card__body">
                    <div className="sl-card__name">
                      <span className="sl-meet-name">{name}</span>
                      {gone && <UnavailablePill />}
                    </div>
                    {/* Experience and languages only, as the talent network card (no role line). */}
                    <div className="sl-card__facts">
                      <span>
                        <WorkOutlineOutlinedIcon fontSize="inherit" />
                        {experienceLabel(talent.totalExperience)}
                      </span>
                      <span>
                        <TranslateOutlinedIcon fontSize="inherit" />
                        {speaksLabel(talent.languagesSpoken)}
                      </span>
                    </div>
                    <ToolChips tools={talent.tools} myTools={myTools} firstName={talent.firstName} />
                  </div>
                  <button
                    type="button"
                    className="sl-meet-x"
                    aria-label={`Remove ${name} from your shortlist`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // eslint-disable-next-line no-underscore-dangle
                      remove(talent._id);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="sl-empty">
            <h3>No one shortlisted yet</h3>
            <p>Save as you browse so you don’t lose track of anyone.</p>
            <button type="button" className="sl-meet-btn sl-meet-btn--primary" onClick={() => navigate(routes.talentNetwork)}>
              Browse talent
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MeetingShortlist;
